import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Pagination,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  SortDirection,
  Tooltip,
  TableSortLabel,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  calculateBudgetaryPerformance,
  calculateBudgetaryPerformanceWithoutPercentage,
} from "../../features/clockifyTimeEntries/clockifyTimeEntriesSlice";
import { ClockifyTimeEntry } from "../../features/clockifyTimeEntries/ClockifyTimeEntry";
import { ClockifyTimeEntryHeadCell } from "./ClockifyTimeEntryHeadCell";
import { ClockifyTimeEntrySortField } from "../../features/clockifyTimeEntries/ClockifyTimeEntrySortField";
import TimeFormatter from "../../features/extensions/TimeFormatter";
import { ClockifyTimeEntriesSortConfig } from "../../features/clockifyTimeEntries/ClockifyTimeEntriesSortConfig";
import ExportToExcelButton from "../exportToExcelButton/ExportToExcelButton";
import { ClockifyTimeEntriesExcelExporter } from "../../features/clockifyTimeEntries/ClockifyTimeEntriesExcelExporter";
import { Milestone } from "../../features/projects/Milestone";
import { useAppSelector } from "../../app/hooks";
import {
  selectNonReadClockifyTimeChangedAlerts,
  selectClockifyTimeEntryTimeChangedReadAlerts,
} from "../../features/alerts/alertsSlice";
import ClockifyTimeChangedAlertComponent from "../clockifyTimeChangedAlert/ClockifyTimeChangedAlert";
import { ClockifyTimeEntryTimeChangedAlert } from "../../features/alerts/clockifyTimeChangedAlert/ClockifyTimeEntryTimeChangedAlert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

interface ClockifyTimeEntryTableProps {
  milestoneId?: number;
  selectedMilestone?: Milestone;
  timeEntries: ClockifyTimeEntry[];
  loading: boolean;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
  };
  sortConfig: ClockifyTimeEntriesSortConfig;
  sortApplied: boolean;
  onFetchTimeEntries: (milestoneId: number) => void;
  onSetPage: (page: number) => void;
  onSetPageSize: (pageSize: number) => void;
  onSetSortConfig: (config: ClockifyTimeEntriesSortConfig) => void;
  onSortTimeEntries: () => void;
  isExpanded?: boolean;
  showExpandButton?: boolean;
  onExpandClick?: () => void;
}

const headCells: ClockifyTimeEntryHeadCell[] = [
  { id: "gitLabTaskId", label: "Task Number", numeric: true, width: "9%" },
  { id: "description", label: "Description", numeric: false, width: "25%" },
  { id: "developerName", label: "Developer", numeric: false, width: "5%" },
  { id: "typeOfWork", label: "Type of Work", numeric: false, width: "8%" },
  {
    id: "timeSpentInHours",
    label: "Time Spent",
    numeric: true,
    width: "9%",
  },
  {
    id: "totalSpentTimeOnTaskInHours",
    label: "Total Time",
    numeric: true,
    width: "10%",
  },
  { id: "taskTimeBudget", label: "Time Budget", numeric: true, width: "12%" },
  {
    id: "budgetaryPerformance",
    label: "Budgetary Performance",
    numeric: true,
    width: "8%",
  },
  { id: "dateRange", label: "Date Range", numeric: false, width: "11%" },
  {
    id: "percentageComplete",
    label: "% Complete",
    numeric: true,
    width: "12%",
  },
];

const pageSizeOptions = [10, 20, 50, 100];

const ClockifyTimeEntryTable = ({
  milestoneId,
  selectedMilestone,
  timeEntries,
  loading,
  pagination,
  sortConfig,
  sortApplied,
  onFetchTimeEntries,
  onSetPage,
  onSetPageSize,
  onSetSortConfig,
  onSortTimeEntries,
  isExpanded = true,
  showExpandButton = false,
  onExpandClick,
}: ClockifyTimeEntryTableProps) => {
  const [isExportToExcelLoading, setIsExportToExcelLoading] =
    useState<boolean>(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const timeFormatter = new TimeFormatter();
  const tableRef = useRef<HTMLDivElement>(null);
  const clockifyTimeChangedAlerts = useAppSelector(
    selectNonReadClockifyTimeChangedAlerts
  );
  const clockifyTimeChangedReadAlerts = useAppSelector(
    selectClockifyTimeEntryTimeChangedReadAlerts
  );

  const hasTimeChanged = (timeEntryId: number) => {
    return clockifyTimeChangedAlerts.some(
      (alert) => alert.clockifyTimeEntryId === timeEntryId
    );
  };

  const getTimeEntryAlerts = (timeEntryId: number) => {
    return clockifyTimeChangedAlerts.filter(
      (alert) => alert.clockifyTimeEntryId === timeEntryId
    );
  };

  const isAlertRead = (alert: ClockifyTimeEntryTimeChangedAlert) => {
    return clockifyTimeChangedReadAlerts.some(
      (readAlert) => readAlert.id === alert.id
    );
  };

  const renderAlertsTooltip = (timeEntryId: number) => {
    const alerts = getTimeEntryAlerts(timeEntryId);
    if (alerts.length === 0) return null;

    return (
      <Box
        sx={{
          p: 1.5,
          bgcolor: "rgba(255, 244, 229, 0.98)",
          border: "1px solid",
          borderColor: "warning.light",
          borderRadius: 1,
          boxShadow: (theme) => theme.shadows[2],
          maxWidth: "none",
          "& .MuiTypography-root": {
            color: "text.primary",
          },
        }}
      >
        {alerts.map((alert) => (
          <ClockifyTimeChangedAlertComponent key={alert.id} alert={alert} />
        ))}
      </Box>
    );
  };

  const handleSortRequest = useCallback(
    (field: ClockifyTimeEntrySortField) => {
      let direction: SortDirection = "desc";

      if (sortConfig.field === field) {
        direction = sortConfig.direction === "asc" ? "desc" : "asc";
      }

      onSetSortConfig({ field, direction });
      onSortTimeEntries();
    },
    [sortConfig, onSetSortConfig, onSortTimeEntries]
  );

  useEffect(() => {
    if (typeof milestoneId === "number" && milestoneId > 0) {
      onFetchTimeEntries(milestoneId);
    }
  }, [milestoneId, onFetchTimeEntries]);

  const handlePageChange = (
    page: number
  ) => {
    onSetPage(page);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    onSetPageSize(Number(event.target.value));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const shouldShowTypeOfWork = useMemo(() => {
    if (timeEntries.length === 0) return true;
    return timeEntries.some(
      (entry) => entry.typeOfWork && entry.typeOfWork !== "N/A"
    );
  }, [timeEntries]);

  const shouldShowPercentageComplete = useMemo(() => {
    if (timeEntries.length === 0) return true;
    return timeEntries.some(
      (entry) =>
        entry.percentageComplete !== undefined &&
        entry.percentageComplete !== null &&
        entry.percentageComplete > 0
    );
  }, [timeEntries]);

  const visibleHeadCells = useMemo(() => {
    let filtered = headCells;
    if (!shouldShowTypeOfWork) {
      filtered = filtered.filter((cell) => cell.id !== "typeOfWork");
    }
    if (!shouldShowPercentageComplete) {
      filtered = filtered.filter((cell) => cell.id !== "percentageComplete");
    }
    return filtered;
  }, [shouldShowTypeOfWork, shouldShowPercentageComplete]);

  const paginatedTimeEntries = timeEntries.slice(
    (pagination.currentPage - 1) * pagination.pageSize,
    pagination.currentPage * pagination.pageSize
  );

  if (loading && !timeEntries.length) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!timeEntries.length) {
    return (
      <Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, px: 0.5 }}
        >
          {showExpandButton && (
            <IconButton onClick={onExpandClick} size="small" sx={{ mr: 1 }}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          <Typography variant="h6" component="h2">
            Clockify Time Entries
          </Typography>
        </Box>
        <Collapse in={isExpanded}>
          <Typography sx={{ mt: 2 }}>
            No time entries available for this milestone.
          </Typography>
        </Collapse>
      </Box>
    );
  }

  const clockifyTimeEntriesExcelExporter =
    new ClockifyTimeEntriesExcelExporter();
  const handleExportToExcel = async () => {
    setIsExportToExcelLoading(true);
    try {
      clockifyTimeEntriesExcelExporter.exportToExcel(
        timeEntries,
        `Clockify Time Entries - ${selectedMilestone?.name}`
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setIsExportToExcelLoading(false);
    }
  };
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexDirection: isSmallScreen ? "column" : "row",
          px: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {showExpandButton && (
            <IconButton onClick={onExpandClick} size="small" sx={{ mr: 1 }}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          <Typography variant="h6" component="h2">
            Clockify Time Entries
          </Typography>
        </Box>
        <ExportToExcelButton
          isLoading={isExportToExcelLoading}
          onClick={handleExportToExcel}
        />
      </Box>
      <Collapse in={isExpanded}>
        <Paper
          elevation={3}
          sx={{ mb: 3, overflow: "hidden", position: "relative" }}
          ref={tableRef}
        >
          {loading && timeEntries.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(13, 17, 23, 0.7)"
                    : "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <TableContainer
            sx={{
              height: {
                xs: 400,
                sm: 500,
                md: 600,
              },
              overflow: "auto",
              "& .MuiTableHead-root": {
                position: "sticky",
                top: 0,
                zIndex: 10,
              },
            }}
          >
            <Table aria-label="clockify time entries table" stickyHeader>
              <TableHead>
                <TableRow>
                  {visibleHeadCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align="left"
                      sortDirection={
                        sortConfig.field === headCell.id && sortApplied
                          ? sortConfig.direction
                          : false
                      }
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        position: "relative",
                        width: headCell.width,
                        zIndex: 2,
                      }}
                      onClick={() =>
                        !headCell.disableSorting &&
                        handleSortRequest(headCell.id)
                      }
                    >
                      {!headCell.disableSorting ? (
                        <TableSortLabel
                          active={sortConfig.field === headCell.id}
                          direction={
                            sortConfig.field === headCell.id
                              ? sortConfig.direction
                              : "asc"
                          }
                        >
                          {headCell.label}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTimeEntries.map((entry: ClockifyTimeEntry) => {
                  const budgetaryPerformance = shouldShowPercentageComplete
                    ? calculateBudgetaryPerformance(entry)
                    : calculateBudgetaryPerformanceWithoutPercentage(entry);
                  const hasTimeChange = hasTimeChanged(entry.id);

                  return (
                    <Tooltip
                      key={entry.id}
                      title={renderAlertsTooltip(entry.id)}
                      placement="top"
                      arrow
                      disableHoverListener={getTimeEntryAlerts(entry.id).every(
                        isAlertRead
                      )}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: "transparent",
                            p: 0,
                            maxWidth: "max-content",
                            "& .MuiTooltip-arrow": {
                              color: "warning.light",
                              "&::before": {
                                bgcolor: "rgba(255, 244, 229, 0.98)",
                                border: "1px solid",
                                borderColor: "warning.light",
                              },
                            },
                          },
                        },
                      }}
                    >
                      <TableRow
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          backgroundColor:
                            hasTimeChange &&
                            !getTimeEntryAlerts(entry.id).every(isAlertRead)
                              ? "rgba(255, 152, 0, 0.1)"
                              : "inherit",
                          "&:hover": {
                            backgroundColor:
                              hasTimeChange &&
                              !getTimeEntryAlerts(entry.id).every(isAlertRead)
                                ? "rgba(255, 152, 0, 0.2)"
                                : "inherit",
                          },
                        }}
                      >
                        <TableCell align="left">
                          {entry.gitLabTaskId || "N/A"}
                        </TableCell>
                        <TableCell align="left">
                          {entry.description &&
                          entry.description.length > 74 ? (
                            <Tooltip
                              title={
                                <Typography
                                  variant="body2"
                                  style={{ whiteSpace: "pre-line" }}
                                >
                                  {entry.description}
                                </Typography>
                              }
                              arrow
                              placement="bottom"
                            >
                              <Typography
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  lineHeight: 1.43,
                                  letterSpacing: "0.01071em",
                                  fontWeight: 400,
                                  fontSize: "0.875rem",
                                }}
                              >
                                {entry.description}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                lineHeight: 1.43,
                                letterSpacing: "0.01071em",
                                fontWeight: 400,
                                fontSize: "0.875rem",
                              }}
                            >
                              {entry.description || "N/A"}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {entry.developerName || "N/A"}
                        </TableCell>
                        {shouldShowTypeOfWork && (
                          <TableCell align="left">
                            {entry.typeOfWork || "N/A"}
                          </TableCell>
                        )}
                        <TableCell align="left">
                          <Box component="span">
                            {timeFormatter.toHoursAndMinutes(
                              entry.timeSpentInHours
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          <Box
                            component="span"
                            sx={{
                              color: (theme) =>
                                typeof entry.taskTimeBudget === "number" &&
                                entry.taskTimeBudget > 0 &&
                                entry.totalSpentTimeOnTaskInHours >
                                  entry.taskTimeBudget
                                  ? theme.palette.warning.main
                                  : "inherit",
                            }}
                          >
                            {entry.totalSpentTimeOnTaskInHours === 0
                              ? "N/A"
                              : timeFormatter.toHoursAndMinutes(
                                  entry.totalSpentTimeOnTaskInHours
                                )}
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          {typeof entry.taskTimeBudget === "number" &&
                          entry.taskTimeBudget > 0
                            ? timeFormatter.toHoursAndMinutes(
                                entry.taskTimeBudget
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px solid",
                              borderColor:
                                budgetaryPerformance === 0
                                  ? "grey"
                                  : budgetaryPerformance <= 1
                                  ? "success.main"
                                  : budgetaryPerformance <= 1.25
                                  ? "warning.main"
                                  : "error.main",
                              borderRadius: "16px",
                              padding: "0 8px",
                              height: "24px",
                              lineHeight: "20px",
                              color:
                                budgetaryPerformance === 0
                                  ? "grey"
                                  : budgetaryPerformance <= 1
                                  ? "success.main"
                                  : budgetaryPerformance <= 1.25
                                  ? "warning.main"
                                  : "error.main",
                            }}
                          >
                            {`${budgetaryPerformance.toFixed(2)}`}
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          {formatDate(entry.dateStart)} -{" "}
                          {formatDate(entry.dateEnd)}
                        </TableCell>
                        {shouldShowPercentageComplete && (
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid",
                                borderColor:
                                  entry.percentageComplete === 0
                                    ? "grey"
                                    : entry.percentageComplete === 100
                                    ? "success.main"
                                    : entry.percentageComplete >= 75
                                    ? "info.main"
                                    : entry.percentageComplete >= 50
                                    ? "warning.main"
                                    : "primary.main",
                                borderRadius: "16px",
                                padding: "0 8px",
                                height: "24px",
                                lineHeight: "20px",
                                color:
                                  entry.percentageComplete === 0
                                    ? "grey"
                                    : entry.percentageComplete === 100
                                    ? "success.main"
                                    : entry.percentageComplete >= 75
                                    ? "info.main"
                                    : entry.percentageComplete >= 50
                                    ? "warning.main"
                                    : "primary.main",
                              }}
                            >
                              {`${entry.percentageComplete}%`}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    </Tooltip>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Box sx={{ flex: { xs: "1", sm: "1 1 25%" } }}>
              <Typography variant="body2" color="text.secondary">
                Page {pagination.currentPage} of {pagination.totalPages} (
                {pagination.totalItems} items total)
              </Typography>
            </Box>

            <Box
              sx={{
                flex: { xs: "1", sm: "1 1 50%" },
                display: "flex",
                justifyContent: "center",
                order: { xs: 3, sm: 2 },
                mt: { xs: 2, sm: 0 },
              }}
            >
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={(_, value) => handlePageChange(value)}
                color="primary"
                size={isSmallScreen ? "small" : "medium"}
                showFirstButton
                showLastButton
                siblingCount={isSmallScreen ? 0 : 1}
                sx={{
                  "& .MuiPaginationItem-root": {
                    minWidth: { xs: 30, sm: 32 },
                    height: { xs: 30, sm: 32 },
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                flex: { xs: "1", sm: "1 1 25%" },
                display: "flex",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                order: { xs: 2, sm: 3 },
              }}
            >
              <FormControl
                variant="outlined"
                size="small"
                sx={{ minWidth: { xs: 150, sm: 120 } }}
              >
                <InputLabel id="page-size-select-label">
                  Items per page
                </InputLabel>
                <Select
                  labelId="page-size-select-label"
                  id="page-size-select"
                  value={pagination.pageSize}
                  onChange={handlePageSizeChange}
                  label="Items per page"
                >
                  {pageSizeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default ClockifyTimeEntryTable;
